/* =============================================================================
   options - the options resolver: ONE deterministic precedence for every
   component:
     static defaults  <-  data-* attributes  <-  constructor argument.
   It also reads the compact `data-options="k:v, k2:'s'"` attribute shape that
   markup uses to configure a component inline.
   SECURITY: NO eval, NO new Function anywhere in this file. The attribute is
   read by a small tolerant tokenizer, and a bare identifier resolves to a global
   BY NAME LOOKUP only - markup can NAME a function but can never get code
   executed through this parser. Parsing never throws.

   A REJECTED ENTRY IS AN ERROR, NOT A WARNING. Dropping one silently is the worst
   outcome available: the component mounts, the screen looks finished, and the
   behaviour the option asked for simply never happens - a section that lazily loads
   its grid on expand stays empty for good, and nothing in the UI says why. So a
   rejection goes to console.error, which the browser gates already treat as a
   failure. It is deliberately NOT a throw: mounting must not abort a page because
   one attribute is mis-authored.

   Exports: coerce · isOn · parseDataOptions · resolveOptions. No dependencies.
   ============================================================================= */

/** Report a rejected data-options entry. Loud by design - see the header. */
function reject(key, tok, why) {
  console.error('[ics] data-options: ' + why + ' - "' + key + '" was DROPPED (value: ' + tok + ')');
}

/** Coerce a data-attribute string into boolean/number/null/JSON/string. */
export function coerce(v) {
  if (v === '' || v === 'true') return v === 'true' ? true : '';
  if (v === 'false') return false;
  if (v === 'null') return null;
  if (v !== '' && !isNaN(v) && v.trim() !== '') return Number(v);
  if ((v[0] === '{' && v.endsWith('}')) || (v[0] === '[' && v.endsWith(']'))) {
    try { return JSON.parse(v); } catch { /* fall through - keep raw string */ }
  }
  return v;
}

/**
 * Boolean state truthiness for an OPTION VALUE - the counterpart to the two
 * producers above and below, which is why it lives here rather than in any one
 * component.
 *
 * ⚠ THE THREE AUTHORED ROUTES DO NOT AGREE, AND ONLY ONE OF THEM IS A TRAP:
 *   data-disabled="false"            -> coerce()            -> boolean false  ✔
 *   data-options="disabled:false"    -> the BARE-TOKEN arm  -> boolean false  ✔
 *   data-options="disabled:'false'"  -> the QUOTED-STRING arm -> the STRING 'false'
 * A bare truthiness test reads that last one as ON and silently disables the field
 * the markup meant to leave alone. Gate every state read on this, so all three
 * routes mean the same thing.
 *
 * It lives here ONCE, beside the producers of the ambiguous value. A component
 * never carries a private copy and never reads a state option bare - including
 * through an alias such as `const o = this.options; if (o.disabled)`, which a
 * search for `options.disabled` cannot see.
 */
export const isOn = (v) => !!v && v !== 'false' && v !== '0' && v !== '';

/**
 * Parse a `data-options` value: a brace-less object literal such as
 *   width:250, required:true, prompt:'pick one', formatter:myFormatFn
 * Supported values: numbers, true/false/null, 'single'/"double" strings, [..]/{..}
 * JSON blocks, and bare identifiers (resolved from globalThis by name - lookup only,
 * dotted paths included). An unknown identifier or an unparseable entry is dropped
 * and reported with a console.error - never a throw, never an eval.
 * @returns {object}
 */
export function parseDataOptions(text) {
  const out = {};
  if (!text) return out;
  let i = 0; const n = text.length;
  const ws = () => { while (i < n && /\s/.test(text[i])) i++; };
  while (i < n) {
    ws();
    const keyM = /^[A-Za-z_$][\w$]*/.exec(text.slice(i));
    if (!keyM) break;
    const key = keyM[0]; i += key.length; ws();
    if (text[i] !== ':') break;
    i++; ws();
    let value, ok = true;
    const c = text[i];
    if (c === "'" || c === '"') {                    // quoted string
      let j = i + 1, s = '';
      while (j < n && text[j] !== c) { s += text[j] === '\\' ? text[++j] : text[j]; j++; }
      value = s; i = j + 1;
    } else if (c === '{' || c === '[') {             // balanced JSON block
      const open = c, close = c === '{' ? '}' : ']';
      let depth = 0, j = i, inS = null;
      for (; j < n; j++) {
        const ch = text[j];
        if (inS) { if (ch === '\\') j++; else if (ch === inS) inS = null; continue; }
        if (ch === "'" || ch === '"') inS = ch;
        else if (ch === open) depth++;
        else if (ch === close && --depth === 0) { j++; break; }
      }
      const block = text.slice(i, j);
      try { value = JSON.parse(block.replace(/'/g, '"').replace(/([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g, '$1"$2":')); }
      catch { ok = false; reject(key, block, 'unparseable block'); }
      i = j;
    } else {                                          // bare token: number / bool / null / identifier(.path)
      const tokM = /^[^,]+/.exec(text.slice(i));
      const tok = (tokM ? tokM[0] : '').trim(); i += tokM ? tokM[0].length : 0;
      if (tok === 'true') value = true;
      else if (tok === 'false') value = false;
      else if (tok === 'null') value = null;
      else if (tok !== '' && !isNaN(tok)) value = Number(tok);
      else if (/^[A-Za-z_$][\w$]*(\.[A-Za-z_$][\w$]*)*$/.test(tok)) {
        value = tok.split('.').reduce((o, k) => (o == null ? o : o[k]), globalThis);  // name lookup ONLY
        if (value === undefined) { ok = false; reject(key, tok, 'unknown identifier'); }
      } else { ok = false; reject(key, tok, /^function\b|^\(|=>/.test(tok) ? 'a function literal cannot be written here - bind the handler in script' : 'unsupported value'); }
    }
    if (ok) out[key] = value;
    ws();
    if (text[i] === ',') i++;
  }
  return out;
}

/**
 * The single option-resolution path:
 *   Ctor.defaults  <-  data-* attributes (data-options included)  <-  ctor options.
 * Only keys DECLARED in Ctor.defaults are read from individual data-* attributes,
 * so an unrelated data- attribute on the element can never become an option.
 * data-options and the ctor argument may introduce extra keys - the options bag
 * is open by design.
 */
export function resolveOptions(Ctor, el, options) {
  const defaults = Ctor.defaults || {};
  const fromData = {};
  if (el && el.dataset) {
    for (const key of Object.keys(defaults)) {
      if (key in el.dataset) fromData[key] = coerce(el.dataset[key]);
    }
    if (el.dataset.options) Object.assign(fromData, parseDataOptions(el.dataset.options));
  }
  return Object.assign({}, defaults, fromData, options || {});
}
